// src/stores/auth.ts
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/auth'
import { usersApi } from '@/api/users'
import type { User } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  const isLoggedIn = ref<boolean>(localStorage.getItem('isLoggedIn') === 'true')
  const isEmailVerified = ref<boolean>(localStorage.getItem('isEmailVerified') === 'true')
  const token = ref<string>(localStorage.getItem('authToken') || '')
  const userRole = ref<string>(localStorage.getItem('userRole') || '')
  const user = ref<User>({
    id: localStorage.getItem('userId') || '1',
    name: localStorage.getItem('userName') || 'User',
    email: localStorage.getItem('userEmail') || 'user@cloud.com'
  })

  const isAuthenticated = computed(() => isLoggedIn.value)
  const needsEmailVerification = computed(() => isLoggedIn.value && !isEmailVerified.value)

  // Инициализация состояния из localStorage
  function init(): void {
    const hasValidToken = checkTokenValidity()
    isLoggedIn.value = hasValidToken
    isEmailVerified.value = localStorage.getItem('isEmailVerified') === 'true'
    token.value = localStorage.getItem('authToken') || ''
    userRole.value = localStorage.getItem('userRole') || ''

    if (hasValidToken) {
      const decoded = decodeToken(token.value)
      if (decoded) {
        user.value = {
          id: decoded.sub,
          name: localStorage.getItem('userName') || decoded.sub,
          email: localStorage.getItem('userEmail') || `${decoded.sub}@cloud.com`
        }
      }
    }
  }

  // Декодирование JWT токена
  function decodeToken(jwtToken: string): { sub: string; role: string; iat: number; exp: number } | null {
    try {
      const base64Url = jwtToken.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      console.error('Failed to decode token:', error)
      return null
    }
  }

  // Проверка валидности токена
  function checkTokenValidity(): boolean {
    const jwtToken = localStorage.getItem('authToken')
    if (!jwtToken) return false

    try {
      const decoded = decodeToken(jwtToken)
      if (!decoded) return false

      const validRoles = ['user', 'admin']
      if (!validRoles.includes(decoded.role)) return false

      const now = Math.floor(Date.now() / 1000)
      if (decoded.exp && decoded.exp < now) return false

      return true
    } catch (error) {
      return false
    }
  }

  // ✅ Вход с использованием API
  async function loginWithToken(jwtToken: string): Promise<boolean> {
    const decoded = decodeToken(jwtToken)
    if (!decoded) return false

    const validRoles = ['user', 'admin']
    if (!validRoles.includes(decoded.role)) return false

    token.value = jwtToken
    userRole.value = decoded.role
    isLoggedIn.value = true
    isEmailVerified.value = true

    localStorage.setItem('authToken', jwtToken)
    localStorage.setItem('userRole', decoded.role)
    localStorage.setItem('isLoggedIn', 'true')
    localStorage.setItem('isEmailVerified', 'true')
    localStorage.setItem('userName', decoded.sub)
    localStorage.setItem('userId', decoded.sub)

    user.value = {
      id: decoded.sub,
      name: decoded.sub,
      email: localStorage.getItem('userEmail') || `${decoded.sub}@cloud.com`
    }

    return true
  }

  // ✅ Вход через API
  async function login(loginParam: string, password: string): Promise<boolean> {
    try {
      const response = await authApi.login({ login: loginParam, password })

      if (response.success && response.data?.token) {
        await loginWithToken(response.data.token)
        return true
      }
      return false
    } catch (error) {
      console.error('Login error:', error)
      return false
    }
  }

  // ✅ Регистрация через API
  async function register(name: string, login: string, email: string, password: string): Promise<boolean> {
    try {
      const response = await authApi.registration({ name, login, email, password })
      return response.success
    } catch (error) {
      console.error('Registration error:', error)
      return false
    }
  }

  // ✅ Подтверждение email через API
  async function confirmEmail(mail: string, code: string): Promise<boolean> {
    try {
      const response = await authApi.confirmEmail({ mail, code })
      if (response.success) {
        isEmailVerified.value = true
        localStorage.setItem('isEmailVerified', 'true')
        return true
      }
      return false
    } catch (error) {
      console.error('Email confirmation error:', error)
      return false
    }
  }

  // ✅ Загрузка профиля пользователя через API
  async function fetchUserProfile(): Promise<boolean> {
    try {
      const response = await usersApi.getProfile()

      if (response.success && response.data) {
        user.value = {
          id: response.data.id,
          name: response.data.name,
          email: response.data.email
        }
        userRole.value = response.data.role

        localStorage.setItem('userName', response.data.name)
        localStorage.setItem('userEmail', response.data.email)
        localStorage.setItem('userRole', response.data.role)

        return true
      }
      return false
    } catch (error) {
      console.error('Fetch profile error:', error)
      return false
    }
  }

  function verifyEmail(): void {
    isEmailVerified.value = true
    localStorage.setItem('isEmailVerified', 'true')
  }

  function logout(): void {
    isLoggedIn.value = false
    isEmailVerified.value = false
    token.value = ''
    userRole.value = ''

    localStorage.removeItem('isLoggedIn')
    localStorage.removeItem('isEmailVerified')
    localStorage.removeItem('authToken')
    localStorage.removeItem('userRole')
    localStorage.removeItem('userEmail')
    localStorage.removeItem('userName')
    localStorage.removeItem('userId')
  }

  function updateUser(name: string, email: string): void {
    if (email !== user.value.email) {
      isEmailVerified.value = false
      localStorage.removeItem('isEmailVerified')
    }
    user.value.name = name
    user.value.email = email
    localStorage.setItem('userName', name)
    localStorage.setItem('userEmail', email)
  }

  // Получение заголовков для API запросов с токеном
  function getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    if (token.value) {
      headers['Authorization'] = `Bearer ${token.value}`
    }
    return headers
  }

  return {
    isLoggedIn,
    isEmailVerified,
    token,
    userRole,
    user,
    isAuthenticated,
    needsEmailVerification,
    init,
    decodeToken,
    checkTokenValidity,
    loginWithToken,
    login,
    register,
    confirmEmail,
    fetchUserProfile,
    verifyEmail,
    logout,
    updateUser,
    getAuthHeaders
  }
})